import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/page-header';
import { Plus } from 'lucide-react';
import { VacationRequest } from '../types';
import { formatDate } from '../utils/formatters';
import {
  MyVacationRequests,
  VacationReviewDialog,
  VacationCancelDialog,
  VacationBalanceCards,
  VacationApprovalSummary,
  VacationFilters,
  RequestsTable,
  TeamUsageTable,
  VacationPagination,
} from '../components/vacations';
import {
  useVacationRequests,
  useMyVacationRequests,
  useVacationBalance,
  useTeamUsageSummary,
  useApproveVacationRequest,
  useRejectVacationRequest,
  useCancelVacationRequest,
  useUsers,
  useDebounce,
  useIsResourceAdmin,
  useIsManager,
  useMyReportSubtreeIds,
} from '../hooks';

const ALL_STATUSES = 'ALL';
const ITEMS_PER_PAGE = 100;
// Server-side page size for the manager/admin requests & team-usage grids.
const PAGE_SIZE = 10;
// Unfiltered fetch size backing the aggregate summary tiles.
const SUMMARY_FETCH_LIMIT = 100;

const Vacations: React.FC = () => {
  const { t } = useTranslation('vacations');
  const { user } = useAuth();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const { isAdmin, isLoading: adminLoading } = useIsResourceAdmin();
  const { isManager, isLoading: managerLoading } = useIsManager();
  const { subtreeIds, isLoading: subtreeLoading } = useMyReportSubtreeIds();
  const canManage = isAdmin || isManager;

  // Requests table filters (manager/admin) — applied server-side.
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES);
  const [requestsMemberName, setRequestsMemberName] = useState('');
  const debouncedRequestsMember = useDebounce(requestsMemberName, 300);
  const [requestsPage, setRequestsPage] = useState(1);

  // Team usage filters (manager/admin) — applied server-side.
  const [usageMemberName, setUsageMemberName] = useState('');
  const debouncedUsageMember = useDebounce(usageMemberName, 300);
  const [usagePage, setUsagePage] = useState(1);

  // Reset to the first page whenever a filter changes.
  useEffect(() => setRequestsPage(1), [statusFilter, debouncedRequestsMember]);
  useEffect(() => setUsagePage(1), [debouncedUsageMember]);

  // Review dialog state
  const [reviewRequest, setReviewRequest] = useState<VacationRequest | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewReadOnly, setReviewReadOnly] = useState(false);

  // Cancel dialog state
  const [cancelRequest, setCancelRequest] = useState<VacationRequest | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const { data: users = [] } = useUsers(false);

  // ---- Data: manager/admin (org/team requests) vs user (own requests) ----
  // Requests grid: server-side paginated + filtered by status/member name.
  const { data: manageData, isLoading: manageLoading } = useVacationRequests(
    {
      status: statusFilter !== ALL_STATUSES ? statusFilter : undefined,
      memberName: debouncedRequestsMember,
      page: requestsPage,
      limit: PAGE_SIZE,
    },
    canManage,
  );
  // Unfiltered team requests feeding the aggregate summary tiles.
  const { data: summaryData, isLoading: summaryLoading } = useVacationRequests(
    { page: 1, limit: SUMMARY_FETCH_LIMIT },
    canManage,
  );
  const { data: myData, isLoading: myLoading } = useMyVacationRequests(
    canManage ? '' : user?.id || '',
    1,
    ITEMS_PER_PAGE,
  );

  // Team usage summary (manager/admin reporting): server-side paginated + searched.
  const { data: usageData, isLoading: usageLoading } = useTeamUsageSummary(
    {
      year: currentYear,
      search: debouncedUsageMember,
      page: usagePage,
      limit: PAGE_SIZE,
    },
    canManage,
  );

  // Balance for the current (self) user, shown on the user view
  const { data: myBalance, isLoading: balanceLoading } = useVacationBalance(
    user?.id || '',
    currentYear,
    !canManage,
  );

  // Balance for the member under review (manager/admin). Use the year of the
  // request's start date rather than the current calendar year so the balance
  // matches the year the vacation is booked against.
  const reviewYear = reviewRequest ? new Date(reviewRequest.startDate).getFullYear() : currentYear;
  const { data: reviewBalance, isLoading: reviewBalanceLoading } = useVacationBalance(
    reviewRequest?.memberId || '',
    reviewYear,
    reviewDialogOpen && !!reviewRequest && !reviewReadOnly,
  );

  const approve = useApproveVacationRequest();
  const reject = useRejectVacationRequest();
  const cancel = useCancelVacationRequest();

  const memberNameById = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((u) => map.set(u.id, u.name));
    return map;
  }, [users]);

  // The requests grid is scoped/filtered/paginated server-side, so render its
  // page as-is. The member ("My Requests") view stays newest-first, client-side.
  const requestRows = manageData?.requests ?? [];

  const myRequests = useMemo(
    () =>
      (myData?.requests ?? [])
        .slice()
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [myData],
  );

  // Aggregate summary tiles are derived from the (unfiltered) team requests.
  // Managers are already scoped server-side; keep a defensive subtree filter.
  const summaryRequests = useMemo(() => {
    const source = summaryData?.requests ?? [];
    return isManager && !isAdmin
      ? source.filter((req) => subtreeIds.has(req.memberId))
      : source;
  }, [summaryData, isManager, isAdmin, subtreeIds]);

  const usageRows = usageData?.summaries ?? [];

  const clearRequestsFilters = () => {
    setStatusFilter(ALL_STATUSES);
    setRequestsMemberName('');
  };

  const listLoading =
    adminLoading || managerLoading || subtreeLoading || (canManage ? manageLoading : myLoading);

  const handleReviewClick = (request: VacationRequest) => {
    setReviewRequest(request);
    setReviewReadOnly(false);
    setReviewDialogOpen(true);
  };

  const handleViewClick = (request: VacationRequest) => {
    setReviewRequest(request);
    setReviewReadOnly(true);
    setReviewDialogOpen(true);
  };

  const handleCancel = (request: VacationRequest) => {
    setCancelRequest(request);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async (reason: string) => {
    if (!cancelRequest) return;
    try {
      await cancel.mutateAsync({
        id: cancelRequest.id,
        data: reason ? { reason } : undefined,
      });
      setCancelDialogOpen(false);
      setCancelRequest(null);
    } catch (error) {
      console.error('Error cancelling vacation request:', error);
    }
  };

  const handleApprove = async (comments: string) => {
    if (!reviewRequest) return;
    try {
      await approve.mutateAsync({ id: reviewRequest.id, data: comments ? { comments } : {} });
      setReviewDialogOpen(false);
      setReviewRequest(null);
    } catch (error) {
      console.error('Error approving vacation request:', error);
    }
  };

  const handleReject = async (comments: string) => {
    if (!reviewRequest) return;
    try {
      await reject.mutateAsync({ id: reviewRequest.id, data: { comments } });
      setReviewDialogOpen(false);
      setReviewRequest(null);
    } catch (error) {
      console.error('Error rejecting vacation request:', error);
    }
  };

  return (
    <div className="space-y-6" data-testid="vacations-page">
      <PageHeader
        title={canManage ? t('approvals.title') : t('title')}
        description={canManage ? t('approvals.description') : t('description')}
        testId="vacations-header"
      >
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => navigate('/vacations/admin')} data-testid="vacations-button-admin">
              {t('adminSettings')}
            </Button>
          )}
          <Button onClick={() => navigate('/vacations/new')} data-testid="vacations-button-new">
            <Plus className="mr-2 h-4 w-4" />
            {t('newRequest')}
          </Button>
        </div>
      </PageHeader>

      {canManage ? (
        <>
          <VacationApprovalSummary requests={summaryRequests} loading={summaryLoading} />

          <RequestsTable
            requests={requestRows}
            members={users}
            loading={listLoading}
            currentUserId={user?.id}
            onReview={handleReviewClick}
            onView={handleViewClick}
            onCancel={handleCancel}
            filters={
              <VacationFilters
                statusFilter={statusFilter}
                memberNameFilter={requestsMemberName}
                showMemberFilter
                onStatusChange={setStatusFilter}
                onMemberNameChange={setRequestsMemberName}
                onClear={clearRequestsFilters}
              />
            }
            footer={
              <VacationPagination
                currentPage={manageData?.page ?? 1}
                totalPages={manageData?.totalPages ?? 1}
                totalItems={manageData?.total ?? 0}
                itemsPerPage={PAGE_SIZE}
                onPageChange={setRequestsPage}
              />
            }
          />

          <div className="space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('approvals.teamUsageTitle')} — {formatDate(new Date(), { month: 'long', year: 'numeric' })}
            </h2>
            <TeamUsageTable
              summaries={usageRows}
              loading={usageLoading}
              filters={
                <VacationFilters
                  memberNameFilter={usageMemberName}
                  showMemberFilter
                  showStatusFilter={false}
                  onMemberNameChange={setUsageMemberName}
                  onClear={() => setUsageMemberName('')}
                />
              }
              footer={
                <VacationPagination
                  currentPage={usageData?.page ?? 1}
                  totalPages={usageData?.totalPages ?? 1}
                  totalItems={usageData?.total ?? 0}
                  itemsPerPage={PAGE_SIZE}
                  onPageChange={setUsagePage}
                />
              }
            />
          </div>
        </>
      ) : (
        <>
          <VacationBalanceCards balance={myBalance} loading={balanceLoading} />
          <MyVacationRequests
            requests={myRequests}
            loading={listLoading}
            isSubmitting={cancel.isPending}
            onCancel={handleCancel}
          />
        </>
      )}

      <VacationReviewDialog
        request={reviewRequest}
        memberName={reviewRequest ? memberNameById.get(reviewRequest.memberId) : undefined}
        balance={reviewBalance}
        balanceLoading={reviewBalanceLoading}
        open={reviewDialogOpen}
        readOnly={reviewReadOnly}
        isSubmitting={approve.isPending || reject.isPending}
        onOpenChange={(open) => {
          setReviewDialogOpen(open);
          if (!open) {
            setReviewRequest(null);
            setReviewReadOnly(false);
          }
        }}
        onApprove={handleApprove}
        onReject={handleReject}
      />

      <VacationCancelDialog
        request={cancelRequest}
        open={cancelDialogOpen}
        isSubmitting={cancel.isPending}
        onOpenChange={(open) => {
          setCancelDialogOpen(open);
          if (!open) setCancelRequest(null);
        }}
        onConfirm={handleConfirmCancel}
      />
    </div>
  );
};

export default Vacations;
